import React, { useMemo } from 'react';
import ConfirmModal from '../../shared/modal/components/ConfirmModal';
import useConfirmModal from '../../shared/modal/hooks/useConfirmModal';
import LinkPickerDialog from '../../shared/linkPicker/components/LinkPickerDialog';
import useLinkPicker from '../../shared/linkPicker/hooks/useLinkPicker';
import { CmsContext } from '../definitions';
import { setConfig } from '../../store/rootReducer';
import { useDispatch } from 'react-redux';
/**
 * SURF-4809: `showLinkSearch` is no longer a prop.
 *
 * It used to be handed in from Angular's `root_controller`, which built it out of the
 * `internalLinkService` that lived in the `wysiwyg` plugin. That service and its modal are
 * now React (`shared/linkPicker`), so the context supplies the function itself and the
 * `show-link-search` attribute is gone from `root.html`.
 *
 * The function's signature is unchanged, `(currentHref, allowEmbeddedMenus)` resolving to
 * `{ returnVal, targetLabel, sso }` or `null`, so the three LinkFinder components and
 * everything else reading it off the context did not have to change.
 */
const CmsReactContext = ({
  children,
  showAudienceModal,
  showImportDialog,
  notifyContentUpdated,
  cmsConfig,
  selectedSubtenant,
  selectedTenant,
  addReport,
  getReports,
}) => {
  const { confirm, confirmProps } = useConfirmModal();
  const { showLinkSearch, linkPickerProps } = useLinkPicker();
  const dispatch = useDispatch();
  const cmsContext = useMemo(() => {
    dispatch(setConfig({ config: cmsConfig }));
    return {
      cmsConfig,
      selectedSubtenant,
      selectedTenant,
      confirm,
      showLinkSearch,
      showAudienceModal,
      notifyContentUpdated,
      showImportDialog,
      addReport,
      getReports,
    };
  }, [
    cmsConfig,
    selectedSubtenant,
    selectedTenant,
    showLinkSearch,
    showAudienceModal,
    showImportDialog,
    addReport,
    getReports,
    dispatch,
  ]);

  return (
    <CmsContext.Provider value={cmsContext}>
      {children}
      <ConfirmModal {...confirmProps} />
      {/*
        Mounted only while open, and keyed per open.

        Angular's `$modal.open` built a fresh scope and a fresh controller every time, so the
        picker always started empty. A permanently-mounted React component with a `visible`
        prop does not: every `useState` survives the close, so the next open paints the
        previous link's type, target and chained selectors, with Save already enabled, until
        the `getNavTypes` round trip lands. Clicking Save in that window wrote the previous
        field's link into the new field, and if `getNavTypes` failed or returned an empty
        list the stale selection never cleared at all.

        `openId` increments on every open, so React unmounts and remounts rather than
        reusing the instance. That is what restores the Angular guarantee.
      */}
      {linkPickerProps.visible && (
        <LinkPickerDialog key={linkPickerProps.openId} {...linkPickerProps} />
      )}
    </CmsContext.Provider>
  );
};

export default CmsReactContext;
